import { ArrowUp } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import {
  POSITION_SCALE,
  SURFACE_PADDING,
  SIZE_SCALE,
  RADIUS_SCALE,
  SHADOW_SCALE,
  INTERACTIVE_STATES,
  Z_INDEX,
  COLOR_SCALE,
  OPACITY,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useTranslation } from '@/hooks/useTranslation';

const ScrollToTop = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      return;
    }

    // Use requestAnimationFrame instead of setTimeout to avoid violations
    let rafId: number | null = null;
    let startTime: number | null = null;
    const HIDE_DELAY_MS = 300;

    const checkTime = (currentTime: number) => {
      startTime ??= currentTime;

      const elapsed = currentTime - startTime;
      if (elapsed >= HIDE_DELAY_MS) {
        setShouldRender(false);
      } else {
        rafId = requestAnimationFrame(checkTime);
      }
    };

    rafId = requestAnimationFrame(checkTime);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isVisible]);

  // Calculate scroll progress and show button when scrolled down 500px
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;

      setScrollProgress(Math.min(scrollPercent, 100));

      if (scrollTop > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Add keyboard shortcut support (Up arrow key with Shift)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Shift + Up arrow scrolls to top (to avoid interfering with normal scrolling)
      if (e.key === 'ArrowUp' && e.shiftKey && window.scrollY > 100) {
        e.preventDefault();
        scrollToTop();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const scrollToTop = () => {
    setIsScrolling(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    // Reset scrolling state after animation
    setTimeout(() => setIsScrolling(false), 1000);
  };

  if (!shouldRender && !isVisible) {
    return null;
  }

  return (
    <div
      className={`fixed fixed-safe-bottom ${POSITION_SCALE.right['6']} transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16 pointer-events-none'
      }`}
      style={{ zIndex: Z_INDEX.modal }}
    >
      {/* Progress Ring */}
      <div className="relative mb-2">
        <svg
          className={`${SIZE_SCALE.w12} ${SIZE_SCALE['12']} transform -rotate-90`}
          viewBox="0 0 36 36"
        >
          {/* Background circle */}
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="2"
          />
          {/* Progress circle */}
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="rgba(59, 130, 246, 0.8)"
            strokeWidth="2"
            strokeDasharray={`${scrollProgress * 0.991}, 100`}
            strokeLinecap="round"
            className="transition-all duration-300"
          />
        </svg>

        {/* Progress percentage */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-white">{Math.round(scrollProgress)}%</span>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`relative ${SURFACE_PADDING['3']} ${COLOR_SCALE['shortcuts-bg']} ${COLOR_SCALE['text-on-dark']} ${RADIUS_SCALE.full} ${SHADOW_SCALE.lg} ${COLOR_SCALE['status-teal-hover']} hover:${SHADOW_SCALE.xl} hover:scale-110 active:scale-95 transition-all ${INTERACTIVE_STATES.duration.slow} ${INTERACTIVE_STATES.focus.outline} ${INTERACTIVE_STATES.focus.ring} ${INTERACTIVE_STATES.focus.offset} ${
          isScrolling ? 'animate-pulse' : ''
        }`}
        aria-label={t('aria', 'scroll_to_top', 'Scroll to top')}
      >
        <ArrowUp
          className={`${SIZE_SCALE['5']} ${SIZE_SCALE['5']} transition-transform duration-200 ${
            isScrolling ? 'animate-bounce' : ''
          }`}
        />

        {/* Ripple effect on click */}
        <div
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          className={`${POSITION_SCALE.absolute} ${POSITION_SCALE.inset} ${RADIUS_SCALE.full} bg-white/20 scale-0 ${OPACITY[0]} animate-ping`}
          style={{ animationDuration: '0.6s' }}
        ></div>
      </button>
    </div>
  );
};

const ScrollToTopWithMemoryLeakMonitor = withMemoryLeakMonitor(ScrollToTop, {
  componentName: 'ScrollToTop',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  renderThreshold: 30,
  frequencyThreshold: 5,
});

export default ScrollToTopWithMemoryLeakMonitor;
